/* Organizer Website — mandatory, AUTO-POPULATED auctions section.
   Every public auction of the organizer appears here with correct state
   (live / upcoming / results). The organizer configures PRESENTATION only
   (cards vs timeline, order, page size, featured) — never builds it manually. */
import { ArrowRight, ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EventStatusBadge } from "@/components/EventStatusBadge";
import { HorseIcon } from "@/components/brand/HorseIcon";
import { ResilientImage } from "@/components/media/ResilientImage";
import { mediaUrlFromMap } from "@/lib/api";
import { formatDate, localized } from "@/lib/intl";
import { eventBase } from "@/lib/microsite";

const orderEvents = (events, order) => {
  const ts = (e) => (e.starts_at ? new Date(e.starts_at).getTime() : 0);
  return [...events].sort((a, b) => (order === "newest" ? ts(b) - ts(a) : ts(a) - ts(b)));
};

const EventCard = ({ event, organizer, media, locale, t, featured = false }) => {
  const hero = mediaUrlFromMap(media, event.hero_media_id);
  const ended = event.status === "ended";
  return (
    <Link
      to={eventBase(organizer.slug, event.slug)}
      data-testid={`website-auction-${event.slug}`}
      className={`group rounded-2xl border border-black/5 bg-white overflow-hidden hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col ${
        featured ? "sm:col-span-2 lg:col-span-2" : ""
      }`}
    >
      <div className={`relative overflow-hidden bg-[#F4F4F2] ${featured ? "h-56 lg:h-72" : "h-44"}`}>
        {hero ? (
          <ResilientImage
            src={hero}
            alt=""
            className="w-full h-full"
            imgClassName="group-hover:scale-[1.03] transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            testId={`website-auction-cover-${event.slug}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" data-testid={`website-auction-cover-fallback-${event.slug}`}>
            <HorseIcon className="w-10 h-10 text-black/10" aria-hidden="true" />
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-2">
          <EventStatusBadge status={event.status} />
          {featured && (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white" style={{ backgroundColor: "var(--brand-accent, #1D4D3B)" }} data-testid="website-auction-featured-badge">
              {t("web.featured")}
            </span>
          )}
        </div>
        <h3 className={`font-medium text-[#0A0A0A] leading-snug brand-heading ${featured ? "text-2xl" : "text-lg"}`}>
          {localized(event.title, locale)}
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#737373]">
          {event.starts_at && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
              {formatDate(event.starts_at, locale)}
            </span>
          )}
          {event.venue?.city && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
              {[event.venue.city, event.venue.country].filter(Boolean).join(", ")}
            </span>
          )}
          {event.lot_count > 0 && <span>{t("web.lotCount", { count: event.lot_count })}</span>}
        </div>
        <span className="mt-auto pt-2 inline-flex items-center gap-1 text-sm font-medium text-[#0A0A0A] group-hover:text-[var(--brand-accent,#1D4D3B)] transition-colors duration-200">
          {ended ? t("web.viewResults") : t("web.viewAuction")}
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};

const TimelineRow = ({ event, organizer, locale, t }) => {
  const ended = event.status === "ended";
  const d = event.starts_at ? new Date(event.starts_at) : null;
  return (
    <li className="relative flex gap-6" data-testid={`website-auction-${event.slug}`}>
      <div className="flex flex-col items-center pt-1.5">
        <span
          className={`w-3 h-3 rounded-full shrink-0 ${event.status === "live" ? "animate-pulse" : ""}`}
          style={{ backgroundColor: ended ? "#C9C9C4" : "var(--brand-accent, #1D4D3B)" }}
          aria-hidden="true"
        />
        <span className="w-px flex-1 bg-black/8" aria-hidden="true" />
      </div>
      <Link to={eventBase(organizer.slug, event.slug)} className="group flex-1 pb-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 min-w-0">
        <div className="w-20 shrink-0 flex flex-col">
          <span className="text-2xl font-light tabular-nums text-[#0A0A0A] leading-none brand-heading">
            {d ? d.getDate() : "—"}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/45">
            {d ? d.toLocaleDateString(locale, { month: "short", year: "numeric" }) : ""}
          </span>
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5 flex-wrap">
            <EventStatusBadge status={event.status} />
            {event.lot_count > 0 && <span className="text-xs text-[#737373]">{t("web.lotCount", { count: event.lot_count })}</span>}
          </div>
          <h3 className="text-lg font-medium text-[#0A0A0A] leading-snug truncate brand-heading">{localized(event.title, locale)}</h3>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-[#0A0A0A] group-hover:text-[var(--brand-accent,#1D4D3B)] transition-colors duration-200">
          {ended ? t("web.viewResults") : t("web.viewAuction")}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
};

export const WebsiteAuctions = ({ organizer, website, events, media }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const cfg = website?.auctions || {};
  const [showAll, setShowAll] = useState(false);

  const { active, ended } = useMemo(() => {
    const featuredId = cfg.featured_event_id;
    const act = orderEvents(events.filter((e) => ["published", "live"].includes(e.status)), cfg.order || "soonest");
    // live first, then featured, then configured order
    act.sort((a, b) => {
      const w = (e) => (e.status === "live" ? 0 : e.id === featuredId ? 1 : 2);
      return w(a) - w(b);
    });
    return { active: act, ended: orderEvents(events.filter((e) => e.status === "ended"), "newest") };
  }, [events, cfg.order, cfg.featured_event_id]);

  const perPage = cfg.per_page || 9;
  const visibleActive = showAll ? active : active.slice(0, perPage);
  const visibleEnded = cfg.show_ended !== false ? (showAll ? ended : ended.slice(0, Math.max(3, perPage - visibleActive.length))) : [];
  const hiddenCount = active.length + (cfg.show_ended !== false ? ended.length : 0) - visibleActive.length - visibleEnded.length;
  const isFeatured = (e, i) => (cfg.featured_event_id ? e.id === cfg.featured_event_id : i === 0 && e.status === "live");

  return (
    <section id="auctions" className="scroll-mt-20 bg-[#FAFAF8] border-y border-black/5" data-testid="website-auctions">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 flex flex-col gap-12">
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--brand-accent, #1D4D3B)" }}>
            {organizer.name}
          </span>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] brand-heading">{t("web.auctionsTitle")}</h2>
        </div>

        {active.length === 0 && ended.length === 0 ? (
          <p className="text-sm text-[#737373]" data-testid="website-auctions-empty">{t("web.noAuctions")}</p>
        ) : cfg.layout === "timeline" ? (
          <ol className="flex flex-col" data-testid="website-auctions-timeline">
            {[...visibleActive, ...visibleEnded].map((e) => (
              <TimelineRow key={e.id} event={e} organizer={organizer} locale={locale} t={t} />
            ))}
          </ol>
        ) : (
          <div className="flex flex-col gap-14" data-testid="website-auctions-cards">
            {visibleActive.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleActive.map((e, i) => (
                  <EventCard key={e.id} event={e} organizer={organizer} media={media} locale={locale} t={t} featured={isFeatured(e, i)} />
                ))}
              </div>
            )}
            {visibleEnded.length > 0 && (
              <div className="flex flex-col gap-6" data-testid="website-auctions-results">
                <h3 className="text-base font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/45">{t("web.pastAuctions")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visibleEnded.map((e) => (
                    <EventCard key={e.id} event={e} organizer={organizer} media={media} locale={locale} t={t} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!showAll && hiddenCount > 0 && (
          <button
            type="button"
            data-testid="website-auctions-show-all"
            onClick={() => setShowAll(true)}
            className="self-center inline-flex items-center h-11 px-7 rounded-full border border-black/15 text-sm font-medium text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200"
          >
            {t("web.showAllAuctions", { count: hiddenCount })}
          </button>
        )}
      </div>
    </section>
  );
};
